import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ModalController, NavParams } from '@ionic/angular';
import { Subject, takeUntil } from 'rxjs';
import { FeatureFlagFacade } from 'src/app/core/state/facades/feature-flag.facade';
import {
  EXCEEDED_RATE_OTHER_REASON,
  EXCEEDED_RATE_REASONS,
} from 'src/app/pages/agreements/constants/exceeded-rate-reasons.contants';
import { AgreementsService } from 'src/app/pages/agreements/services/agreements.service';
import { ExceededRate, ExceededRateReason } from 'src/app/pages/agreements/types/exceeded-rate';
import { FeatureFlagEnum } from 'src/app/shared/enums/feature-flag.enum';
import { UtilityService } from 'src/app/shared/services/utility.service';

@Component({
  selector: 'app-agreement-interruptive-modal',
  templateUrl: './agreement-interruptive-modal.component.html',
  styleUrls: ['./agreement-interruptive-modal.component.scss'],
})
export class AgreementInterruptiveModalComponent implements OnInit, OnDestroy {
  // Form group
  rateExceedForm: FormGroup;

  // Exposing the rate exceeded reason options for template use
  readonly EXCEEDED_RATE_REASONS = EXCEEDED_RATE_REASONS;

  // Exposing the 'Other' rate exceeded reason option for template use
  readonly EXCEEDED_RATE_OTHER_REASON = EXCEEDED_RATE_OTHER_REASON;

  // Mable client fee
  mableClientFee: string;

  // Rates that exceeds the NDIS max amount
  rates: ExceededRate[] = [];

  // subject for onDestroy, used to unsubscribe from subscription
  onDestroySubject = new Subject<void>();

  // flag that indicates whether the form validation has been checked
  hasFormValidationBeenChecked = false;

  featureFlags = {
    isValidatePriceRateBatchEnabled: false,
  };

  constructor(
    private modalCtrl: ModalController,
    private navParams: NavParams,
    private agreementService: AgreementsService,
    public utilityService: UtilityService,
    private featureFlagFacade: FeatureFlagFacade,
  ) {}

  async ngOnInit(): Promise<void> {
    await this.fetchFeatureFlags();
    this.setupFormFields();
    this.listenFormValueChange();

    // getting the needed properties that have been passed to the modal
    this.rates = this.navParams.get('exceededCapRates');
    this.mableClientFee = this.navParams.get('mableClientFee');
  }

  ionViewWillEnter() {
    // always reset this variable to false for validation purposes
    this.hasFormValidationBeenChecked = false;
  }

  /**
   * Update the subject on destroying the component.
   */
  ngOnDestroy(): void {
    this.onDestroySubject.next();
    this.onDestroySubject.complete();
  }

  /**
   * Sets up form group and its controls.
   */
  private setupFormFields(): void {
    this.rateExceedForm = new FormGroup({
      rateExceedReasons: new FormControl('', [Validators.required]),
      otherTextField: new FormControl(''),
    });
  }

  /**
   * Submits the reason why rates are exceeding the NDIS max limit in case the form has been filled correctly.
   * Finally closes the modal flagging that the 'Continue with current rates' button has been clicked by the user.
   */
  async submit(): Promise<void> {
    const isFormValid = this.checkFormValidation();
    if (!isFormValid) return;
    const exceededRateReasonRequestObj: ExceededRateReason = {
      reason: this.rateExceedForm.get('rateExceedReasons').value.title,
      carerId: this.navParams.get('carerId'),
      consumerId: this.navParams.get('consumerId'),
      reasonOtherText: this.rateExceedForm.get('otherTextField').value || null,
    };
    try {
      await this.agreementService.saveExceededRateReason(exceededRateReasonRequestObj);
    } catch (error) {
      console.error('Saving the exceeding rate reason has failed', error);
    } finally {
      this.modalCtrl.dismiss('continue');
    }
  }

  /**
   * Closes the modal.
   */
  close(): void {
    this.modalCtrl.dismiss();
  }

  /**
   * Checks whether the form is valid.
   *
   * @returns true if the form is valid; false otherwise.
   */
  private checkFormValidation(): boolean {
    if (!this.rateExceedForm.get('rateExceedReasons').valid) {
      this.rateExceedForm.get('rateExceedReasons').markAllAsTouched();
    }
    if (!this.rateExceedForm.get('otherTextField').valid) {
      this.rateExceedForm.get('otherTextField').markAllAsTouched();
    }
    this.hasFormValidationBeenChecked = true;
    return this.rateExceedForm.valid;
  }

  /**
   * Subscribe to form value changes to update the validations.
   */
  private listenFormValueChange(): void {
    this.rateExceedForm
      .get('rateExceedReasons')
      .valueChanges.pipe(takeUntil(this.onDestroySubject))
      .subscribe((value) => {
        // In case user selects 'Other' option, insert validation
        if (value.description === 'Other') {
          this.hasFormValidationBeenChecked = false;
          this.rateExceedForm.get('otherTextField').setValidators([Validators.required]);
        } else {
          // Otherwise, remove validation from 'Other' textarea field and delete its text
          this.rateExceedForm.get('otherTextField').setValidators([]);
          this.rateExceedForm.get('otherTextField').setValue('');
        }
        this.rateExceedForm.get('otherTextField').updateValueAndValidity();
      });
  }

  /**
   * Opens Qualtrics form.
   * This form is needed when SP wants to request a non-standard budget support item to be added to a rate.
   */
  openQualtricsForm(): void {
    const consumerUuid = this.navParams.get('consumerUuid');
    const carerUuid = this.navParams.get('carerUuid');
    const qualtrixFormUrl = `https://mable.qualtrics.com/jfe/form/SV_86NlaKRhys8PP4q?origin=platform&s=${carerUuid}&c=${consumerUuid}`;
    this.utilityService.openExternalLink(qualtrixFormUrl);
  }

  private async fetchFeatureFlags(): Promise<void> {
    const featureFlags = await this.featureFlagFacade.isFeatureEnabled([
      FeatureFlagEnum.NETWORKERS_PRICE_RATE_VALIDATION_BATCH,
    ]);

    this.featureFlags = {
      isValidatePriceRateBatchEnabled: featureFlags.networkers_price_rate_validation_batch,
    };
  }
}
